/**
 * ============================================================================
 * Iron Meridian - Main Menu Component
 * ============================================================================
 * 
 * Title screen with game options: New Game, Load Game, Settings, Help, Patch Notes.
 * Premium war-room aesthetic with smooth animations.
 */

import { useState, memo } from 'react'
import { useAudio } from '../audio/hooks/useAudio.js'
import { clearAllSiteData } from '../services/api.js'
import SaveGamePanel from './SaveGamePanel.jsx'

// Game version - increment with each release
export const GAME_VERSION = '0.12.0'

// Patch notes history
const PATCH_NOTES = [
  {
    version: '0.12.0',
    date: '2026-01-09',
    title: 'Crisis Chains & Escalation System',
    changes: [
      'Multi-Turn Crisis System - Dynamic crises that evolve over 4 stages based on player response',
      '5 Crisis Types: Economic Collapse, Border Conflict, Tech Espionage, Alliance Fracture, Environmental Catastrophe',
      'Escalation mechanics - crises worsen with inaction, bad decisions, or worsening conditions',
      'Multiple resolution paths - resolve through economy, diplomacy, military, or international aid',
      'Aftermath effects - temporary modifiers (3-10 turns) apply after crisis resolution',
      'AI Crisis Awareness - AI recognizes crises and adjusts strategy intelligently',
      'Cross-system integration - crises affect economy, stability, diplomacy, and military',
      'Enhanced Turn Summary - prominent crisis cards with stage indicators and escalation warnings',
      'Visual feedback - color-coded severity, pulsing alerts, progress bars for crisis stages',
      'Deterministic system - same seed + actions = same crisis outcomes for replayability',
      'No instant failure - crises create pressure but are always recoverable',
      'Full documentation in CRISIS_CHAINS_SYSTEM.md'
    ]
  },
  {
    version: '0.11.0',
    date: '2026-01-08',
    title: 'National Doctrine System & AI Balance',
    changes: [
      'National Doctrine System - 8 emergent strategic identities (Militarist, Economic, Technocratic, etc.)',
      'Doctrines derived from behavior: stats, combat, diplomacy, tech choices over last 10 turns',
      'Doctrine modifiers: ±5-12% combat, ±3-8% economy, tech cost reduction',
      'Doctrine compatibility affects diplomatic trust between nations',
      'AI decisions now influenced by dominant doctrines',
      'Country Panel displays top 3 doctrines with progress bars and descriptions',
      'Balanced AI personality distribution - more Opportunists, fewer all-Aggressive games',
      'AI personalities now use relative stat comparison instead of absolute thresholds',
      'Fixed all country names - no more UNK codes or 3-letter abbreviations (AUS→Australia, etc.)',
      'Complete documentation in NATIONAL_DOCTRINE_IMPLEMENTATION.md'
    ]
  },
  {
    version: '0.10.0',
    date: '2026-01-07',
    title: 'Hybrid Country Stat Generation',
    changes: [
      'Deterministic stat generation from game seed - perfect replayability',
      'Tier system: 3 Superpowers, 12 Major Powers, 25 Regional Powers, 160+ Minor nations',
      'Regional modifiers for 6 geographic regions (Europe, Asia, Americas, Africa, Middle East, Oceania)',
      'AI personalities derived from stats (Aggressive/Economic/Defensive/Opportunist)',
      '150+ neighbor definitions - comprehensive border relationships worldwide',
      'No hardcoded stats - all countries generated dynamically',
      'Infinite variety - different seeds create unique world configurations',
      'Full test coverage - 30+ unit and integration tests'
    ]
  },
  {
    version: '0.9.0',
    date: '2026-01-04',
    title: 'Advanced Diplomacy System',
    changes: [
      'Global Reputation system - your actions affect worldwide standing (-100 to +100)',
      '5 Reputation Tiers: REVERED, RESPECTED, NEUTRAL, DISLIKED, PARIAH with gameplay effects',
      'Reputation scaling - high reputation amplifies trust gains, low reputation amplifies penalties',
      '5 Diplomacy Actions: Offer Alliance, Offer NAP, Break Treaty, Issue Threat, Improve Relations',
      'AI acceptance based on trust, reputation, and stance with deterministic outcomes',
      'Map Overlays - toggle views for Stances, Alliances, and Treaty partners on globe',
      '8 Diplomacy Events: Diplomatic Crisis, Peace Summit, Alliance Strain, Trade Embargo, more',
      'Enhanced Turn Summary with reputation changes, trust logs, and action outcomes'
    ]
  },
  {
    version: '0.8.1',
    date: '2026-01',
    title: 'Tech Tree UI Fixes & UX Improvements',
    changes: [
      'Research progress now updates after each turn (no more stale display)',
      'Completed techs now show green immediately upon completion',
      'Home button (🏠) now centers map on your country instead of default location',
      'Map control buttons now aligned in single row for cleaner look',
      'Research state persists in browser storage for offline play',
      'Fixed keyboard shortcut T to open/close Tech Tree panel',
      'Clear Site Data option in Settings > Data Management'
    ]
  },
  {
    version: '0.8.0',
    date: '2026-01',
    title: 'Tech Tree & Events System',
    changes: [
      'Branching tech tree with 35+ technologies across Military, Economy, Governance',
      'Mutual exclusivity - choices like Professional Army vs Citizen Militia matter',
      '5 research tiers from Foundation to Supremacy (tier 5 requires turn 30+)',
      'Dynamic events: global pandemics, rebellions, golden ages, resource discovery',
      'Player-choice events: trade proposals, refugee crises, revolutionary movements',
      'Seeded RNG for deterministic, replayable game outcomes',
      'Tech Tree Panel UI - browse, research, track progress',
      'Turn Summary now shows tech completions and events',
      'Dev Diagnostics Panel - active modifiers, event eligibility (press D)'
    ]
  },
  {
    version: '0.7.0',
    date: '2026-01',
    title: 'Diplomacy System',
    changes: [
      'Alliance, NAP, and Ceasefire treaties with other nations',
      'Trust system (0-100) that changes based on diplomatic actions',
      'Betrayal mechanics - breaking treaties gives 15% combat penalty',
      'Alliance trade bonuses (+10% income from allied nations)',
      'AI diplomacy - opponents propose, accept, reject, and break treaties',
      'Diplomacy Panel UI - manage relationships and pending proposals',
      'Turn Summary now shows diplomatic events',
      'Treaty expirations and automatic trust recovery over time'
    ]
  },
  {
    version: '0.6.0',
    date: '2026-01',
    title: 'UX Polish, Tutorial & V2 Game Systems',
    changes: [
      'Turn Summary Modal - See detailed results after each turn',
      'Combat Feedback - Visual victory/defeat effects on the map',
      'Tutorial System - First-time player guidance overlay',
      'Enhanced Tooltips - Stat breakdowns, terrain info, combat odds',
      'Accessibility - Reduced motion support, focus indicators',
      'V2 Combat Engine - Morale, supply lines, terrain modifiers',
      'V2 Economy Engine - Upkeep, investments, war exhaustion',
      'V2 AI Engine - Smarter opponents with 5 personality types'
    ]
  },
  {
    version: '0.5.2',
    date: '2026-01',
    title: 'Music, Improved Ambient & Bug Fixes',
    changes: [
      'Added procedural ambient music with evolving chord progressions',
      'Improved war room ambient - warm pad chords instead of bass drone',
      'Fixed audio icon always showing muted (state sync issue)',
      'Fixed UNKundefined ghost country near Somalia (Somaliland mapping)',
      'Added support for disputed territories (Somaliland, N. Cyprus, Kosovo)',
      'Music and ambient now both play when game starts'
    ]
  },
  {
    version: '0.5.1',
    date: '2026-01',
    title: 'Audio & Country Code Fixes',
    changes: [
      'Fixed audio playback not producing sound (hook state sync issue)',
      'Fixed countries like Brazil/Australia showing as UNK076 (string-to-int parsing)',
      'Ambient audio now auto-initializes on first user interaction',
      'Added console logging for audio debugging',
      'Reorganized README documentation'
    ]
  },
  {
    version: '0.5.0',
    date: '2026-01',
    title: 'Procedural Audio System & Country Tooltip Fix',
    changes: [
      'Added complete procedural audio system with Web Audio API',
      'War room ambience plays automatically when game starts',
      'Audio settings panel with volume sliders per category',
      'Press M to toggle mute (keyboard shortcut)',
      'Combat sounds: victory fanfare, defeat, territory capture',
      'UI sounds: click, hover, confirm, cancel, alert',
      'Fixed tooltips for Brazil and other countries not showing info',
      'All audio is commercial-safe (procedural or CC0)'
    ]
  },
  {
    version: '0.4.0',
    date: '2025-06',
    title: 'Globe Stability & Complete Country Coverage',
    changes: [
      'Added globe rendering contract with structure hash verification',
      'Full 186-country coverage with human-readable names for all territories',
      'Snapshot testing prevents regression in globe rendering',
      'DEV-only diagnostics panel shows contract status and coverage',
      'Foundational lock document protects stable game systems',
      'Fail-fast policy catches rendering issues before they reach production'
    ]
  },
  {
    version: '0.3.2',
    date: '2025-12-28',
    title: 'Local Saves, Smoother Globe Rotation, Expanded Countries',
    changes: [
      'Added singleplayer local save/load (browser localStorage) and enabled Load Game from main menu',
      'Rotation buttons now animate with easing (no more instant jumps)',
      'Reduced globe rendering artifacts by disabling transitions during animations',
      'Expanded country dataset so neutral/grey countries have stats (Natural Earth 110m ISO coverage)'
    ]
  },
  {
    version: '0.3.1',
    date: '2025-12-28',
    title: 'Interactive Globe, Minimap & UX Fixes',
    changes: [
      'Replaced flat world map SVG with D3.js orthographic globe (Natural Earth 110m)',
      'Added rotation controls and interactive minimap for quick navigation',
      'Fixed rendering artifacts during globe rotation/drag',
      'New Game modal now renders via portal to avoid stacking/context issues',
      'Conquered territories now show correct ownership tags'
    ]
  },
  {
    version: '0.3.0',
    date: '2025-12-28',
    title: 'Main Menu & Country Selection Fix',
    changes: [
      'Added main menu title screen with New Game, Load Game, Settings, Help',
      'Fixed country selection bug - New Game now works correctly',
      'Country selection now auto-selects player country on game start',
      'Added "Return to Menu" button in game',
      'Improved API to return countries with game state'
    ]
  },
  {
    version: '0.2.0',
    date: '2025-12-28',
    title: 'UI Polish & Performance',
    changes: [
      'Enhanced WorldMap with ocean gradient background and collapsible legend',
      'Added terrain system with 8 terrain types and combat modifiers',
      'Performance optimizations with React.memo and throttled handlers',
      'Improved zoom controls with configurable bounds',
      'Glass-morphism design for tooltips and panels',
      'Added AI personality colors to map legend'
    ]
  },
  {
    version: '0.1.0',
    date: '2025-12-27',
    title: 'Initial Release',
    changes: [
      'Core game loop with turn-based mechanics',
      'Interactive world map with pan/zoom',
      'Country selection and action queuing',
      'AI opponents with personality-based decision making',
      'Combat system with tech modifiers',
      'Economy and technology progression',
      'Cloudflare Pages serverless deployment'
    ]
  }
]

// Available countries for selection
const PLAYABLE_COUNTRIES = [
  { id: 'USA', name: 'United States', flag: '🇺🇸', difficulty: 'Easy', description: 'Strong military and economy' },
  { id: 'CHN', name: 'China', flag: '🇨🇳', difficulty: 'Easy', description: 'Massive resources and manpower' },
  { id: 'RUS', name: 'Russia', flag: '🇷🇺', difficulty: 'Medium', description: 'Vast territory, strong military' },
  { id: 'DEU', name: 'Germany', flag: '🇩🇪', difficulty: 'Medium', description: 'Economic powerhouse' },
  { id: 'GBR', name: 'United Kingdom', flag: '🇬🇧', difficulty: 'Medium', description: 'Naval tradition, tech focus' },
  { id: 'JPN', name: 'Japan', flag: '🇯🇵', difficulty: 'Medium', description: 'High technology, limited space' },
  { id: 'IND', name: 'India', flag: '🇮🇳', difficulty: 'Medium', description: 'Large population, growing power' },
  { id: 'BRA', name: 'Brazil', flag: '🇧🇷', difficulty: 'Hard', description: 'Regional power, developing' },
  { id: 'AUS', name: 'Australia', flag: '🇦🇺', difficulty: 'Hard', description: 'Isolated but stable' },
  { id: 'TUR', name: 'Turkey', flag: '🇹🇷', difficulty: 'Hard', description: 'Strategic position, many rivals' },
]

function MainMenu({ onStartGame, hasSavedGame, onLoadGame, onLoadSaveSlot }) {
  const [activePanel, setActivePanel] = useState(null) // 'newGame', 'settings', 'help', 'patchNotes', 'saveFiles'
  const [selectedCountry, setSelectedCountry] = useState('USA')
  const [settings, setSettings] = useState({
    musicVolume: 50,
    sfxVolume: 70,
    showTutorialHints: true,
    autoEndTurn: false,
    confirmActions: true
  })

  const handleStartGame = () => {
    onStartGame(selectedCountry)
  }

  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center bg-war-room-900 relative overflow-hidden">
      {/* Animated background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
        }}
      />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none" 
        style={{ 
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} 
      />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center animate-fade-in">
        {/* Logo/Title */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-white mb-2 tracking-tight">
            <span className="text-war-room-accent">IRON</span> MERIDIAN
          </h1>
          <p className="text-gray-500 text-lg tracking-widest uppercase">Global Strategy • Turn-Based Conquest</p>
          <p className="text-gray-600 text-sm mt-2">Version {GAME_VERSION}</p>
        </div>

        {/* Menu buttons or active panel */}
        {activePanel === null ? (
          <div className="flex flex-col gap-3 w-80">
            <MenuButton 
              onClick={() => setActivePanel('newGame')}
              primary
            >
              <span className="text-xl mr-3">🎮</span>
              New Game
            </MenuButton>
            
            <MenuButton 
              onClick={onLoadGame}
              disabled={!hasSavedGame}
            >
              <span className="text-xl mr-3">💾</span>
              Continue
              {!hasSavedGame && <span className="text-xs text-gray-500 ml-2">(No active game)</span>}
            </MenuButton>

            <MenuButton onClick={() => setActivePanel('saveFiles')}>
              <span className="text-xl mr-3">📂</span>
              Save Files
            </MenuButton>
            
            <MenuButton onClick={() => setActivePanel('settings')}>
              <span className="text-xl mr-3">⚙️</span>
              Settings
            </MenuButton>
            
            <MenuButton onClick={() => setActivePanel('help')}>
              <span className="text-xl mr-3">❓</span>
              How to Play
            </MenuButton>
            
            <MenuButton onClick={() => setActivePanel('patchNotes')}>
              <span className="text-xl mr-3">📋</span>
              Patch Notes
            </MenuButton>
          </div>
        ) : (
          <div className={`w-full ${activePanel === 'saveFiles' ? 'max-w-4xl' : 'max-w-2xl'}`}>
            {/* Back button */}
            <button
              onClick={() => setActivePanel(null)}
              className="mb-4 px-4 py-2 text-gray-400 hover:text-white flex items-center gap-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Menu
            </button>

            {/* Panel content */}
            <div className="bg-war-room-800/90 backdrop-blur-sm rounded-2xl border border-war-room-600/50 shadow-2xl p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {activePanel === 'newGame' && (
                <NewGamePanel
                  countries={PLAYABLE_COUNTRIES}
                  selectedCountry={selectedCountry}
                  onSelectCountry={setSelectedCountry}
                  onStartGame={handleStartGame}
                />
              )}
              
              {activePanel === 'settings' && (
                <SettingsPanel
                  settings={settings}
                  onUpdateSettings={setSettings}
                />
              )}
              
              {activePanel === 'help' && <HelpPanel />}
              
              {activePanel === 'patchNotes' && <PatchNotesPanel notes={PATCH_NOTES} />}

              {activePanel === 'saveFiles' && (
                <SaveGamePanel
                  onLoadSave={(saveData) => {
                    console.log('[MainMenu] Loading save:', saveData?.id || saveData?.displayName)
                    if (onLoadSaveSlot) {
                      onLoadSaveSlot(saveData)
                    }
                    setActivePanel(null)
                  }}
                  onClose={() => setActivePanel(null)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 text-center text-gray-600 text-sm">
        <p>© 2025 Iron Meridian • Developed by DaveTheRave with ❤️</p>
      </div>
    </div>
  )
}

// Menu button component
function MenuButton({ children, onClick, disabled, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full px-6 py-4 rounded-xl font-medium text-left flex items-center
        transition-all duration-200 
        ${disabled 
          ? 'bg-war-room-700/30 text-gray-600 cursor-not-allowed' 
          : primary
            ? 'bg-war-room-accent hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-[1.02]'
            : 'bg-war-room-700/50 hover:bg-war-room-600 text-gray-200 border border-war-room-600/50 hover:border-war-room-500'
        }
      `}
    >
      {children}
    </button>
  )
}

// New Game panel
function NewGamePanel({ countries, selectedCountry, onSelectCountry, onStartGame }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Select Your Nation</h2>
      <p className="text-gray-400 mb-6">Choose wisely - each nation has unique strengths and challenges.</p>
      
      <div className="grid grid-cols-2 gap-3 mb-6">
        {countries.map(country => (
          <button
            key={country.id}
            onClick={() => onSelectCountry(country.id)}
            className={`
              p-4 rounded-xl text-left transition-all
              ${selectedCountry === country.id
                ? 'bg-war-room-accent/20 border-2 border-war-room-accent ring-2 ring-war-room-accent/30'
                : 'bg-war-room-700/50 border border-war-room-600/50 hover:border-war-room-500'
              }
            `}
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{country.flag}</span>
              <div>
                <div className="font-semibold text-white">{country.name}</div>
                <div className={`text-xs font-medium ${
                  country.difficulty === 'Easy' ? 'text-green-400' :
                  country.difficulty === 'Medium' ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {country.difficulty}
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-400">{country.description}</p>
          </button>
        ))}
      </div>

      <button
        onClick={onStartGame}
        className="w-full py-4 bg-war-room-accent hover:bg-blue-600 rounded-xl font-bold text-lg transition-all hover:scale-[1.02] shadow-lg shadow-blue-500/20"
      >
        Start Game as {countries.find(c => c.id === selectedCountry)?.name}
      </button>
    </div>
  )
}

// Settings panel - Integrates with real audio system
function SettingsPanel({ settings, onUpdateSettings }) {
  const {
    isReady,
    isAvailable,
    isMuted,
    settings: audioSettings,
    setVolume,
    toggleMute,
    playUI,
    initializeAudio,
  } = useAudio();

  const updateSetting = (key, value) => {
    onUpdateSettings(prev => ({ ...prev, [key]: value }))
  }

  // Handle first interaction to enable audio
  const handleEnableAudio = async () => {
    const success = await initializeAudio();
    if (success) {
      playUI('confirm');
    }
  };

  // Play test sound
  const handleTestSound = () => {
    playUI('click');
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Settings</h2>
      
      <div className="space-y-6">
        {/* Audio section */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Audio</h3>
          
          {/* Audio not available warning */}
          {!isAvailable && (
            <div className="bg-yellow-900/30 border border-yellow-700 rounded p-3 mb-4">
              <p className="text-yellow-300 text-sm">
                Web Audio API is not supported in your browser.
              </p>
            </div>
          )}

          {/* Enable audio button (shown before first interaction) */}
          {isAvailable && !isReady && (
            <button
              onClick={handleEnableAudio}
              className="w-full bg-war-room-accent hover:bg-blue-600 text-white py-3 px-4 
                       rounded-lg mb-4 transition-colors font-medium"
            >
              🔊 Enable Audio
            </button>
          )}

          {/* Master mute toggle */}
          <div className="flex items-center justify-between py-3 border-b border-war-room-600/50 mb-4">
            <span className="text-white font-medium">Master Audio</span>
            <button
              onClick={toggleMute}
              disabled={!isReady}
              className={`px-4 py-2 rounded-lg transition-colors font-medium
                         ${isMuted 
                           ? 'bg-red-900/50 text-red-300 hover:bg-red-900' 
                           : 'bg-green-900/50 text-green-300 hover:bg-green-900'}
                         disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
            >
              {isMuted ? '🔇 Muted' : '🔊 On'}
            </button>
          </div>

          {/* Volume sliders - using real audio system */}
          <div className="space-y-3">
            <AudioVolumeSlider
              label="Master"
              value={audioSettings.master}
              onChange={(v) => setVolume('master', v)}
              disabled={!isReady || isMuted}
            />
            <AudioVolumeSlider
              label="Music"
              value={audioSettings.music}
              onChange={(v) => setVolume('music', v)}
              disabled={!isReady || isMuted}
            />
            <AudioVolumeSlider
              label="UI Sounds"
              value={audioSettings.ui}
              onChange={(v) => setVolume('ui', v)}
              disabled={!isReady || isMuted}
            />
            <AudioVolumeSlider
              label="Events"
              value={audioSettings.events}
              onChange={(v) => setVolume('events', v)}
              disabled={!isReady || isMuted}
            />
            <AudioVolumeSlider
              label="Ambient"
              value={audioSettings.ambient}
              onChange={(v) => setVolume('ambient', v)}
              disabled={!isReady || isMuted}
            />
          </div>

          {/* Test sound button */}
          <button
            onClick={handleTestSound}
            disabled={!isReady || isMuted}
            className="mt-4 py-2 px-4 bg-war-room-700/50 text-gray-300 rounded-lg 
                     hover:bg-war-room-600 transition-colors text-sm font-medium
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🔔 Test Sound
          </button>

          {/* Keyboard shortcut hint */}
          <p className="text-gray-500 text-xs mt-3">
            Press <kbd className="px-1 py-0.5 bg-war-room-700 rounded">M</kbd> to toggle mute
          </p>
        </div>

        {/* Gameplay section */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Gameplay</h3>
          <div className="space-y-3">
            <SettingToggle
              label="Show Tutorial Hints"
              description="Display helpful tips for new players"
              checked={settings.showTutorialHints}
              onChange={(v) => updateSetting('showTutorialHints', v)}
            />
            <SettingToggle
              label="Auto-End Turn"
              description="Automatically end turn when no actions remain"
              checked={settings.autoEndTurn}
              onChange={(v) => updateSetting('autoEndTurn', v)}
            />
            <SettingToggle
              label="Confirm Actions"
              description="Show confirmation before executing actions"
              checked={settings.confirmActions}
              onChange={(v) => updateSetting('confirmActions', v)}
            />
          </div>
        </div>

        {/* Data Management section */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Data Management</h3>
          <div className="p-3 rounded-lg bg-war-room-700/30">
            <div className="mb-3">
              <div className="text-gray-200">Clear Site Data</div>
              <div className="text-sm text-gray-500">Reset all game progress, research, and settings</div>
            </div>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear all site data? This will permanently delete all game progress, research, and settings.')) {
                  clearAllSiteData()
                  window.location.reload()
                }
              }}
              className="w-full py-2 px-4 bg-red-900/50 text-red-300 rounded-lg 
                       hover:bg-red-800 transition-colors text-sm font-medium
                       flex items-center justify-center gap-2"
            >
              🗑️ Clear All Site Data
            </button>
          </div>
        </div>
      </div>
      
      <p className="mt-6 text-sm text-gray-500 text-center">Settings are saved automatically</p>
    </div>
  )
}

// Audio volume slider component (0-1 scale)
function AudioVolumeSlider({ label, value, onChange, disabled }) {
  const percentage = Math.round(value * 100);
  
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={`text-gray-300 ${disabled ? 'opacity-50' : ''}`}>{label}</span>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min="0"
          max="100"
          value={percentage}
          onChange={(e) => onChange(parseInt(e.target.value) / 100)}
          disabled={disabled}
          className="w-32 accent-war-room-accent disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`${label} volume`}
        />
        <span className={`text-gray-400 w-10 text-right tabular-nums ${disabled ? 'opacity-50' : ''}`}>
          {percentage}%
        </span>
      </div>
    </div>
  )
}

// Setting toggle component
function SettingToggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-war-room-700/30">
      <div>
        <div className="text-gray-200">{label}</div>
        <div className="text-sm text-gray-500">{description}</div>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`
          relative w-12 h-6 rounded-full transition-colors
          ${checked ? 'bg-war-room-accent' : 'bg-war-room-600'}
        `}
      >
        <span className={`
          absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform
          ${checked ? 'translate-x-6' : 'translate-x-0'}
        `} />
      </button>
    </div>
  )
}

// Help panel
function HelpPanel() {
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">How to Play</h2>
      <p className="text-gray-400 mb-6">Master the art of global strategy</p>
      
      <div className="space-y-6">
        <HelpSection
          icon="🎯"
          title="Objective"
          content="Conquer the world by expanding your territory, building your economy, and defeating rival nations. Achieve victory through military dominance, economic supremacy, or technological advancement."
        />
        
        <HelpSection
          icon="🗺️"
          title="Map Navigation"
          content="Click and drag to pan the map. Use scroll wheel or the zoom buttons to zoom in/out. Click on any country to select it and view its details."
        />
        
        <HelpSection
          icon="⚔️"
          title="Combat"
          content="Attack neighboring territories by selecting your country, then clicking Attack on a neighbor. Combat strength is calculated from military power, technology level, and terrain modifiers. Defenders get a 20% bonus."
        />
        
        <HelpSection
          icon="💰"
          title="Economy"
          content="Your economy generates resources each turn. Invest in your economy to increase income, or spend on military and technology. Balance is key to long-term success."
        />
        
        <HelpSection
          icon="🔬"
          title="Technology"
          content="Technology improves your combat effectiveness by 1% per level. Higher tech nations have significant advantages in battle. Invest wisely!"
        />
        
        <HelpSection
          icon="🏛️"
          title="Stability"
          content="Stability represents your nation's internal cohesion. Low stability can lead to unrest and reduced effectiveness. Avoid overexpansion!"
        />
        
        <HelpSection
          icon="🤖"
          title="AI Opponents"
          content="AI nations have different personalities: Aggressive (attacks often), Defensive (fortifies borders), Economic (builds wealth), and Opportunist (exploits weaknesses). Adapt your strategy accordingly!"
        />
      </div>
    </div>
  )
}

// Help section component
function HelpSection({ icon, title, content }) {
  return (
    <div className="flex gap-4">
      <div className="text-3xl">{icon}</div>
      <div>
        <h3 className="font-semibold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{content}</p>
      </div>
    </div>
  )
}

// Patch notes panel
function PatchNotesPanel({ notes }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Patch Notes</h2>
      <p className="text-gray-400 mb-6">Development history and updates</p>
      
      <div className="space-y-6">
        {notes.map((patch, idx) => (
          <div key={patch.version} className={idx > 0 ? 'pt-6 border-t border-war-room-600/50' : ''}>
            <div className="flex items-center gap-3 mb-3">
              <span className="px-2 py-1 bg-war-room-accent/20 text-war-room-accent text-sm font-mono rounded">
                v{patch.version}
              </span>
              <span className="text-gray-500 text-sm">{patch.date}</span>
            </div>
            <h3 className="font-semibold text-white mb-2">{patch.title}</h3>
            <ul className="space-y-1">
              {patch.changes.map((change, i) => (
                <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                  <span className="text-war-room-accent mt-1">•</span>
                  {change}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

export default memo(MainMenu)
